import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import {obsStyles} from '../obsStyle';

function ObsRespiration(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [breaths, setBreaths] = React.useState(allowDefaultValue ? props.breaths : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setBreaths(props.breaths);
        }
    }, []);

    // setup default value
    useEffect(() => {
        if (allowDefaultValue && (_.isUndefined(breaths) || _.isNaN(breaths))) {
            if (_.isUndefined(props.breaths) || props.breaths == null || _.isNaN(props.breaths)) {
                if (type === obsTypes.news) {
                    setBreaths(15);
                } else if (type === obsTypes.pews) {
                    if (ageRangeYears === obsAges._0_1) {
                        setBreaths(50);
                    } else if (ageRangeYears === obsAges._1_5) {
                        setBreaths(40);
                    } else if (ageRangeYears === obsAges._5_12) {
                        setBreaths(30);
                    } else if (ageRangeYears === obsAges._12_18) {
                        setBreaths(20);
                    }
                } else if (type === obsTypes.neuro) {
                    if (ageRangeYears === obsAges._16_99) {
                        setBreaths(15);
                    } else {
                        setBreaths(30);
                    }
                }
            } else {
                setBreaths(props.breaths);
            }
        }
    }, [type, ageRangeYears, props.breaths]);

    const resetObs = (formAcceptSection) => {
        if (!allowDefaultValue && formAcceptSection) {
            setBreaths(null);
            setScore(0);
        }

        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    score,
                    accepted,
                    acceptDate,
                    breaths,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        const checkValue = (!_.isNull(breaths) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [breaths, status]);

    useEffect(() => {
        emitChanged();
    }, [status, breaths, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            newScore = getObsScore('respiratoryRate', type, ageRangeYears, breaths);
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, breaths, type, ageRangeYears]);

    const onBreathsChange = (value) => {
        setBreaths(value);
        resetObs();
    };

    const onStatusChange = (value) => {
        resetObs();
        if (value === status) {
            resetObs();
        } else {
            // console.log(value);
            setStatus(value || obsStatuses.new);
            // acceptObs();
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={breaths}
                            minValue={0}
                            maxValue={100}
                            label={t('Breaths/mins')}
                            onChange={onBreathsChange}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(breaths),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsRespiration.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    breaths: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsRespiration.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsRespiration);